import {Client, ClientUser, Role, UserRole, Workspace} from '../../../API';
import {User} from '../../../utils/typescript/interfaces';

export const TENANT_ID = 'mock-client-1';
export const WORKSPACE_ID = 'mock-ws-1';
export const CLIENT_SLUG = 'meridian';
export const WORKSPACE_SLUG = 'main';

// ── Persona storage key ──────────────────────────────────────────────────────
export const PERSONA_STORAGE_KEY = 'lgc-demo-persona';

// ── User identifiers ─────────────────────────────────────────────────────────
export const USER_SUB = 'mock-sub-1';
export const CLIENT_USER_ID = 'mock-user-1';

export const USER_SUB_2 = 'mock-sub-2';
export const CLIENT_USER_ID_2 = 'mock-user-2';

export const USER_SUB_3 = 'mock-sub-3';
export const CLIENT_USER_ID_3 = 'mock-user-3';

const NOW = new Date('2026-01-01T00:00:00.000Z').toISOString();

// ── Auth users ───────────────────────────────────────────────────────────────
export const mockAuthUser: User = {
    sub: USER_SUB,
    name: 'Jordan',
    family_name: 'Mills',
    email: 'jordan.mills@meridianretail.co',
    'custom:clientId': TENANT_ID,
};

export const mockAuthUser2: User = {
    sub: USER_SUB_2,
    name: 'Alex',
    family_name: 'Chen',
    email: 'alex.chen@meridianretail.co',
    'custom:clientId': TENANT_ID,
};

export const mockAuthUser3: User = {
    sub: USER_SUB_3,
    name: 'Sam',
    family_name: 'Torres',
    email: 'sam.torres@meridianretail.co',
    'custom:clientId': TENANT_ID,
};

// ── Demo personas (for DemoPanel switcher) ───────────────────────────────────
export interface DemoPersona {
    userId: string;
    userSub: string;
    label: string;
    role: string;
    authUser: User;
    isAdmin: boolean;
}

export const DEMO_PERSONAS: DemoPersona[] = [
    {userId: CLIENT_USER_ID,  userSub: USER_SUB,  label: 'Jordan Mills', role: 'Store Manager', authUser: mockAuthUser,  isAdmin: true},
    {userId: CLIENT_USER_ID_2, userSub: USER_SUB_2, label: 'Alex Chen',   role: 'Floor Associate', authUser: mockAuthUser2, isAdmin: false},
    {userId: CLIENT_USER_ID_3, userSub: USER_SUB_3, label: 'Sam Torres',  role: 'New Hire',         authUser: mockAuthUser3, isAdmin: false},
];

export function getCurrentPersonaId(): string {
    try { return localStorage.getItem(PERSONA_STORAGE_KEY) ?? CLIENT_USER_ID; } catch { return CLIENT_USER_ID; }
}

export function getCurrentDemoPersona(): DemoPersona {
    const id = getCurrentPersonaId();
    return DEMO_PERSONAS.find(p => p.userId === id) ?? DEMO_PERSONAS[0];
}

// ── Client ───────────────────────────────────────────────────────────────────
export const mockClient: Client = {
    __typename: 'Client',
    id: TENANT_ID,
    createdAt: NOW,
    created_at: NOW,
    customer_id: null,
    defaultWorkspaces: JSON.stringify([WORKSPACE_ID]),
    deletedAt: null,
    inviteLink: null,
    meta: JSON.stringify({quizzesCount: 0, itemsCount: 0, aiUsageCount: 0}),
    name: 'Meridian Retail Co.',
    ownerUserId: CLIENT_USER_ID,
    slug: CLIENT_SLUG,
    subscription_details: JSON.stringify({}),
    subscription_endsAt: null,
    subscription_plan: 'free',
    subscription_status: 'active',
    updatedAt: NOW,
    userRole: 'owner',
    version: 1,
    feature_flags: JSON.stringify({}),
};

// ── Client users ─────────────────────────────────────────────────────────────
export const mockClientUser: ClientUser = {
    __typename: 'ClientUser',
    id: CLIENT_USER_ID,
    avatarPath: null,
    closedAt: null,
    createdAt: NOW,
    deletedAt: null,
    email: mockAuthUser.email,
    isAdmin: true,
    isConfirmed: true,
    isReadOnly: false,
    lastActive: NOW,
    name: mockAuthUser.name,
    ownerId: TENANT_ID,
    roleUpdatedBy: null,
    roles: '{}',
    sub: USER_SUB,
    surname: mockAuthUser.family_name,
    updatedAt: NOW,
    version: 1,
};

export const mockClientUser2: ClientUser = {
    __typename: 'ClientUser',
    id: CLIENT_USER_ID_2,
    avatarPath: null,
    closedAt: null,
    createdAt: NOW,
    deletedAt: null,
    email: mockAuthUser2.email,
    isAdmin: false,
    isConfirmed: true,
    isReadOnly: false,
    lastActive: NOW,
    name: mockAuthUser2.name,
    ownerId: TENANT_ID,
    roleUpdatedBy: null,
    roles: '{}',
    sub: USER_SUB_2,
    surname: mockAuthUser2.family_name,
    updatedAt: NOW,
    version: 1,
};

export const mockClientUser3: ClientUser = {
    __typename: 'ClientUser',
    id: CLIENT_USER_ID_3,
    avatarPath: null,
    closedAt: null,
    createdAt: NOW,
    deletedAt: null,
    email: mockAuthUser3.email,
    isAdmin: false,
    isConfirmed: true,
    isReadOnly: true,
    lastActive: NOW,
    name: mockAuthUser3.name,
    ownerId: TENANT_ID,
    roleUpdatedBy: null,
    roles: '{}',
    sub: USER_SUB_3,
    surname: mockAuthUser3.family_name,
    updatedAt: NOW,
    version: 1,
};

export const allMockClientUsers: ClientUser[] = [mockClientUser, mockClientUser2, mockClientUser3];

// ── Roles ("Фича Ролей") ─────────────────────────────────────────────────────
export const ROLE_ID_STORE_MANAGER = 'mock-role-store-manager';
export const ROLE_ID_FLOOR_ASSOCIATE = 'mock-role-floor-associate';
export const ROLE_ID_NEW_HIRE = 'mock-role-new-hire';

export const mockRoles: Role[] = [
    {
        __typename: 'Role',
        id: ROLE_ID_STORE_MANAGER,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        key: 'store_manager',
        displayName: 'Store Manager',
        description: 'Full access to store operations and admin settings.',
        createdAt: NOW,
        updatedAt: NOW,
    },
    {
        __typename: 'Role',
        id: ROLE_ID_FLOOR_ASSOCIATE,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        key: 'floor_associate',
        displayName: 'Floor Associate',
        description: 'Day-to-day task lists for floor staff.',
        createdAt: NOW,
        updatedAt: NOW,
    },
    {
        __typename: 'Role',
        id: ROLE_ID_NEW_HIRE,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        key: 'new_hire',
        displayName: 'New Hire',
        description: 'Limited access while onboarding.',
        createdAt: NOW,
        updatedAt: NOW,
    },
];

// Membership for the three demo personas in their matching role above - mirrors
// DEMO_PERSONAS' role labels 1:1. Without this, listUserRoles has nothing to
// return, so role-scoped features (Members' "Access roles" column, and audit
// assignments made to a role rather than a named person) silently show nobody.
export const mockUserRoles: UserRole[] = [
    {__typename: 'UserRole', id: 'mock-userrole-1', ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, userId: CLIENT_USER_ID, roleId: ROLE_ID_STORE_MANAGER, createdAt: NOW, createdBy: CLIENT_USER_ID},
    {__typename: 'UserRole', id: 'mock-userrole-2', ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, userId: CLIENT_USER_ID_2, roleId: ROLE_ID_FLOOR_ASSOCIATE, createdAt: NOW, createdBy: CLIENT_USER_ID},
    {__typename: 'UserRole', id: 'mock-userrole-3', ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, userId: CLIENT_USER_ID_3, roleId: ROLE_ID_NEW_HIRE, createdAt: NOW, createdBy: CLIENT_USER_ID},
];

// ── Workspace ────────────────────────────────────────────────────────────────
export const mockWorkspace: Workspace = {
    __typename: 'Workspace',
    archivedIds: '[]',
    avatarPath: null,
    childIds: '[]',
    createdAt: NOW,
    deletedAt: null,
    id: WORKSPACE_ID,
    name: 'Main',
    ownerId: TENANT_ID,
    pinnedIds: '[]',
    slug: WORKSPACE_SLUG,
    trashedIds: '[]',
    updatedAt: NOW,
    version: 1,
};
